import { Icon } from '@acpaas-ui/react-components';
import PropTypes from 'prop-types';
import { forwardRef, useRef } from 'react';
import ReactInputMask from 'react-input-mask';
import { v4 as uuid } from 'uuid';
import { getCurrentStates, INPUT_STATES } from './forms.utils';

const getClasses = (props, state, hasError, hasWarning) =>
  `spo-a-textfield ${state.state} a-input ${props.size === 'small' ? 'a-input--small' : ''} ${
    props.readOnly ? 'read-only' : ''
  } ${props.disabled ? 'disabled' : ''} ${props.required ? 'is-required' : ''} ${
    props.description ? 'spo-a-textfield--has-description' : ''
  } ${state.isSubmitting || props.disabled || props.iconRight ? 'has-icon-right' : ''} ${hasError ? 'has-error' : ''} ${
    hasWarning ? 'has-warning' : ''
  }`;

const TextField = forwardRef((props, ref) => {
  const {
    label = '',
    placeholder = '',
    value = '',
    name = '',
    iconRight = '',
    mask,
    pattern,
    onChange = () => {},
    onSubmit,
    onCancel = () => {},
    onEditStart = () => {},
    onKeyDown = () => {},
    state = INPUT_STATES.READ,
    hasError = false,
    hasWarning = false,
    readOnly = false,
    disabled = false,
    description = '',
    validationError = ''
  } = props;
  const id = useRef(uuid()).current;
  const { isRead, isSubmitting, isDefault } = getCurrentStates(state);

  const handleEditStart = (e) => {
    if (!readOnly && !disabled && isRead) {
      e.stopPropagation();
      onEditStart();
    }
  };

  const handleChange = (event) => {
    onChange(event?.target?.value);
  };

  const handleKeyDown = (event) => {
    if (isDefault) {
      onKeyDown(event);
      return;
    }

    onKeyDown(event);
    if (event?.code === 'Enter') {
      onSubmit?.();
    }
    if (event?.code === 'Escape') {
      onCancel();
    }
    if (event?.code === 'Enter' || event?.code === 'Escape') {
      document.getElementById(`text-field-${id}`)?.blur?.();
    }
  };

  return (
    <div
      className={getClasses(props, { isSubmitting, state }, hasError || !!validationError, hasWarning)}
      onClick={handleEditStart}
    >
      {label && (
        <label className="a-input__label" htmlFor="text-field">
          {label}
        </label>
      )}
      <div className="a-input__wrapper">
        {mask ? (
          <ReactInputMask
            mask={mask}
            inputRef={ref}
            title={value}
            className={isRead ? 'input-readonly' : ''}
            type="text"
            name={name || 'text-field'}
            id={`text-field-${id}`}
            placeholder={!isRead ? placeholder : ''}
            value={value}
            onChange={readOnly ? undefined : handleChange}
            onFocus={handleEditStart}
            onKeyDown={handleKeyDown}
            aria-describedby={description || 'text-field-readonly'}
            readOnly={readOnly || !onChange}
            disabled={isSubmitting || disabled}
            autoComplete="off"
            pattern={pattern}
          />
        ) : (
          <input
            ref={ref}
            title={value}
            className={isRead ? 'input-readonly' : ''}
            type="text"
            name={name || 'text-field'}
            id={`text-field-${id}`}
            placeholder={!isRead ? placeholder : ''}
            value={value}
            onChange={readOnly ? undefined : handleChange}
            onFocus={handleEditStart}
            onKeyDown={handleKeyDown}
            aria-describedby={description || 'text-field-readonly'}
            readOnly={readOnly || !onChange}
            disabled={isSubmitting || disabled}
            autoComplete="off"
            pattern={pattern}
          />
        )}
        {iconRight && <Icon name={iconRight} />}
        {isSubmitting && <span className="a-spinner a-spinner--sm spinner ai"></span>}
      </div>
      {!hasWarning && (description || validationError) && (
        <small
          className={`${hasError || validationError ? 'u-text-danger' : ''} description`}
          id="text-field-description"
        >
          {validationError || description}
        </small>
      )}
      {!hasError && hasWarning && <small className="u-text-warning">{description}</small>}
      <div className={`actions ${isRead || isDefault ? 'hidden' : ''}`}>
        {/* ACPaaS Button doest support setting tab index */}
        <button
          className="a-button a-button--success a-button--small has-icon"
          aria-label="Submit"
          disabled={!onSubmit || isSubmitting || disabled}
          onClick={() => onSubmit?.()}
          tabIndex={isRead || isDefault ? '-1' : ''}
        >
          <Icon name="ai-check-1" />
        </button>
        <button
          className="a-button a-button--danger a-button--small a-button-outline has-icon"
          aria-label="Cancel"
          disabled={isSubmitting || disabled}
          onClick={onCancel}
          tabIndex={isRead || isDefault ? '-1' : ''}
        >
          <Icon name="ai-close" />
        </button>
      </div>
    </div>
  );
});

TextField.propTypes = {
  state: PropTypes.oneOf(Object.values(INPUT_STATES)),
  value: PropTypes.string,
  onChange: PropTypes.func,
  onCancel: PropTypes.func,
  onEditStart: PropTypes.func,
  onSubmit: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  description: PropTypes.string
};
export default TextField;
